(function () {
  'use strict';

  /* ============================================================
     Aurum Brew — anchor arrival
     assets/anchor-glide.js  (part 2 of 2; part 1 is the inline
     head script in layout/theme.liquid that suppresses the
     browser's own fragment jump)

     WHAT WAS WRONG
     Arriving from a home promise icon put four separate motions on
     the same beat, each unaware of the others:

       1. @view-transition cross-fade from the home page
       2. #MainContent { animation: ab-page-in .6s }
       3. html { scroll-behavior: smooth } turned the browser's
          fragment jump into a long animated scroll
       4. this script, which teleported 520px above the target and
          ran its own 900ms glide on top of (3)

     And the previous version of this file removed `sp-inview` from
     the destination pillar and re-added it at the end of the glide.
     By then the section's own IntersectionObserver had already
     revealed the text, so the text faded in, was yanked back to
     opacity 0, and faded in a second time. That is the "double
     load" — it was this file doing it, not a duplicate render.

     Meanwhile lazy images above the target finished loading during
     all of that and moved the destination, so the scroll chased a
     target that kept sliding.

     WHAT HAPPENS NOW
     One motion, in a fixed order:

       a. The head script clears the hash before the body is parsed,
          so the browser never jumps and never smooth-scrolls, and
          marks <html data-aur-arriving>. The CSS in
          page-transitions.css holds every pillar in its pre-reveal
          state, so no observer can fire mid-flight.
       b. We place the viewport a short lead above the target while
          the page is still fading in, so the reposition is not seen.
       c. One eased glide down, re-measuring every frame so late
          images cannot pull the target away.
       d. On landing: restore the hash, release the hold. The pillar
          reveals once, as the arrival. That is the payoff beat.
     ============================================================ */

  var DURATION   = 1150;  // ms, the glide itself
  var LEAD       = 360;   // px above the target where the glide starts
  var SETTLE     = 260;   // ms to let the page fade before moving
  var IMG_WAIT   = 900;   // ms cap on waiting for images to load

  function headerOffset() {
    var h = document.querySelector('sticky-header, .header-wrapper, .section-header');
    var height = h ? h.getBoundingClientRect().height : 72;
    return height + 24;
  }

  /* Slow to start, unhurried through the middle, settles rather than
     stops. A plain easeOut arrives too abruptly at this distance. */
  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function release(el) {
    /* Two steps, one frame apart, and the order matters.

       The held state carries `transition: none !important`. If the hold
       is dropped and `sp-inview` is added in the same frame, the browser
       can collapse both into a single style recalculation and snap the
       copy in at full opacity instead of transitioning it — the exact
       hard cut this whole change exists to remove. Letting one frame
       pass means the element is first recomputed as "hidden, but now
       transitionable", so the reveal actually interpolates.

       Adding sp-inview is not redundant: the pillar's own observer may
       never have fired, because it was held and off-screen for the whole
       flight. Without this the destination would stay blank. If the
       observer did fire, this is a no-op and the hold removal alone
       plays the reveal. */
    document.documentElement.removeAttribute('data-aur-arriving');

    var done = false;
    function reveal() {
      if (done) return;
      done = true;
      if (el && el.classList && el.classList.contains('sp')) {
        el.classList.add('sp-inview');
      }
    }
    /* requestAnimationFrame does not fire in a backgrounded tab. If the
       visitor opens the link in a background tab, an rAF-only reveal
       would leave the copy held at opacity 0 until they switched to it —
       and the 4s failsafe calls this same function, so it would be
       stranded too. The timer is the guarantee; the frame is the nicety
       that makes the transition interpolate when the tab is visible. */
    requestAnimationFrame(reveal);
    setTimeout(reveal, 120);
  }

  function glideTo(el, opts) {
    opts = opts || {};
    var offset = headerOffset();   // measured once: a header that shrinks
                                   // mid-flight must not retarget us
    var root = document.documentElement;
    var prevBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';   // never compete with CSS smooth

    function measure() {
      return Math.max(0, window.pageYOffset + el.getBoundingClientRect().top - offset);
    }

    var startY = Math.max(0, measure() - LEAD);
    window.scrollTo(0, startY);

    var begin = null, cancelled = false, frame = null;

    function cleanup() {
      root.style.scrollBehavior = prevBehavior;
      ['wheel', 'touchstart', 'keydown'].forEach(function (evt) {
        window.removeEventListener(evt, onUserScroll);
      });
    }

    /* If the visitor takes over, stop fighting them — and release
       immediately so they are never left looking at held, blank text. */
    function onUserScroll() {
      if (cancelled) return;
      cancelled = true;
      if (frame) cancelAnimationFrame(frame);
      cleanup();
      release(el);
      if (opts.hash) restoreHash(opts.hash);
    }
    ['wheel', 'touchstart', 'keydown'].forEach(function (evt) {
      window.addEventListener(evt, onUserScroll, { passive: true, once: true });
    });

    function step(ts) {
      if (cancelled) return;
      if (begin === null) begin = ts;
      var p = Math.min(1, (ts - begin) / DURATION);

      /* Re-measure every frame: an image finishing above the target
         would otherwise move it after we had committed. */
      var live = measure();
      window.scrollTo(0, startY + (live - startY) * easeInOutCubic(p));

      if (p < 1) {
        frame = requestAnimationFrame(step);
        return;
      }

      window.scrollTo(0, measure());   // exact landing, no drift
      cleanup();
      if (opts.hash) restoreHash(opts.hash);

      /* Let the landing settle for one frame, then bloom. Releasing on
         the same frame as the final scrollTo makes the reveal read as
         part of the scroll rather than as the arrival. */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { release(el); });
      });
    }
    frame = requestAnimationFrame(step);
  }

  function restoreHash(hash) {
    try { history.replaceState(null, '', window.location.pathname + window.location.search + hash); }
    catch (e) {}
  }

  function targetFromHash(hash) {
    if (!hash || hash.length < 2) return null;
    try { return document.querySelector(hash); } catch (e) { return null; }
  }

  /* Wait for the images that sit ABOVE the target, since only those can
     move it. Capped, so a slow or broken image cannot strand the visitor
     on a held page. */
  function whenSettled(el, done) {
    var finished = false;
    function go() { if (!finished) { finished = true; done(); } }

    var cutoff = el.getBoundingClientRect().top + window.pageYOffset;
    var pending = [].slice.call(document.images).filter(function (img) {
      return !img.complete && (img.getBoundingClientRect().top + window.pageYOffset) < cutoff;
    });

    var left = pending.length;
    if (!left) { setTimeout(go, SETTLE); return; }

    pending.forEach(function (img) {
      var tick = function () { if (--left <= 0) setTimeout(go, SETTLE); };
      img.addEventListener('load', tick, { once: true });
      img.addEventListener('error', tick, { once: true });
    });
    setTimeout(go, IMG_WAIT);   // hard cap
  }

  /* 1) Arriving from another page (the home promise icons).
        The head script stashed the hash on window.__aurAnchor. */
  function onArrive() {
    var hash = window.__aurAnchor;
    if (!hash) return;
    var el = targetFromHash(hash);
    if (!el) { release(null); restoreHash(hash); return; }
    whenSettled(el, function () { glideTo(el, { hash: hash }); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onArrive);
  } else {
    onArrive();
  }

  /* Safety net: if anything above throws or the target never appears,
     never leave the page held and blank. */
  setTimeout(function () {
    if (document.documentElement.hasAttribute('data-aur-arriving')) {
      release(targetFromHash(window.__aurAnchor));
    }
  }, 4000);

  /* 2) Same-page anchor clicks */
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href*="#"]') : null;
    if (!a || a.hasAttribute('data-no-glide')) return;

    var url;
    try { url = new URL(a.href, window.location.href); } catch (err) { return; }
    if (url.pathname !== window.location.pathname || url.host !== window.location.host) return;

    var el = targetFromHash(url.hash);
    if (!el) return;

    e.preventDefault();

    /* Hold the pillars for a same-page jump too, so the destination
       blooms on arrival rather than part-way through the scroll. */
    var isPillar = el.classList && el.classList.contains('sp');
    if (isPillar) {
      document.documentElement.setAttribute('data-aur-arriving', '');
      el.classList.remove('sp-inview');
    }
    glideTo(el, { hash: url.hash });
  });
})();
